<template>
  <div
    :class="{
      'q-pa-md': true,
      'cursor-pointer': true,
      'q-hoverable': true,
      'dotted-card': true,
      'col-12': true,
      'uploader-area': true,
      'uploader-area-active': isDraggingFile,
    }"
    height="100%"
    min-height="160px"
    @click="showUploadFileInput()"
    @drop.prevent="onDropFile($event)"
    @dragover.prevent="isDraggingFile = true"
    @dragenter.prevent="isDraggingFile = true"
    @dragleave.prevent="isDraggingFile = false"
  >
    <div class="row wrap justify-start q-pa-sm">
      <div class="col-12 q-py-sm text-center justify-center">
        <q-avatar
          color="pastel-blue"
          size="48px"
          class="q-mr-sm"
        >
          <q-icon
            name="las la-cloud-upload-alt"
            color="black"
            size="32px"
            class=""
          />
        </q-avatar>
      </div>
      <div class="col-12 q-py-none q-pt-xs q-pb-md text-center">
        <p class="text-subtitle2 q-mb-none">
          {{ multiple ? i18n.upload_files : i18n.upload_file }}
        </p>
        <span class="text-caption text-seconday">{{ i18n.drag_and_drop }}</span>
      </div>
    </div>

    <UploaderProgress
      :upload-progress="uploadProgress"
      :wait-between-uploads="waitBetweenUploads"
      :number-of-files-to-upload="numberOfFilesToUpload"
      :files-to-upload="filesToUpload.length"
      :all-upload-are-done="allUploadAreDone"
    />
  </div>
  <input
    ref="uploadInput"
    type="file"
    :accept="allowedContentTypes.join(', ')"
    :multiple="multiple"
    @change="uploadInputChange"
  >
</template>

<script setup>
import frQuasar from "quasar/lang/fr";
import enQuasar from "quasar/lang/en-US";

import { ref } from "vue";
import { onBeforeMount } from 'vue';
import { DirectUpload } from "@rails/activestorage";
import { usePage } from "@inertiajs/vue3";
import { getCurrentInstance } from "vue";
import UploaderProgress from "@/shared/uploader_progress.vue";

const instance = getCurrentInstance();
const $q = instance?.proxy?.$q;

const page = usePage();
const emit = defineEmits(["signed_id"]);
const isDraggingFile = ref(false);
const uploadInput = ref(null);

const waitBetweenUploads = ref(false);

onBeforeMount(() => {
  locale(instance?.proxy.$q.lang.isoName);
});

function locale(locale) {
  switch (locale) {
  case enQuasar.isoName:
    i18n.value.drag_and_drop = "drag and drop or click here";
    i18n.value.upload_file = "Upload files";
    i18n.value.upload_file_too_big = function(a, b) { return `The file ${a} is too large. The maximum allowed size is ${b}.`; };
    i18n.value.upload_files = "Upload a file";
    break;
  case frQuasar.isoName:
  default:
    i18n.value.drag_and_drop = "glissez-déposez ou cliquez ici";
    i18n.value.upload_file = "Téléverser un fichier";
    i18n.value.upload_file_too_big = function(a, b) { return `Le fichier ${a} est trop volumineux. La taille maximale autorisée est de ${b}.`; };
    i18n.value.upload_files = "Téléverser des fichiers";
    break;
  }
}


// 1: Handle of selected or dragged files
function showUploadFileInput() {
  uploadInput.value.click();
}

function onDropFile(event) {
  isDraggingFile.value = false;
  if (event.dataTransfer.files.length > 0) {
    handleFilesToUpload(event.dataTransfer.files);
  }
}

function uploadInputChange(event) {
  handleFilesToUpload(event.target.files);
  uploadInput.value.value = null; // value.value is correct. value is a property of the input element.
}

// 2: Handle of files to upload
const filesToUpload = ref([]);
const numberOfFilesToUpload = ref(0);
const uploadProgress = ref({
  progress: 0,
  total: 0,
  percentile: 0,
});

const i18n = ref({});

const props = defineProps({
  allowedContentTypes: {
    type: Array,
    default: () => { return []; },
  },
  csvHeadersRestriction: Array,
  max_size: {
    type: Number,
    default: 104857600, // 100 MiB
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  merge_csv: {
    type: Boolean,
    default: false,
  },
  csv_encoding: {
    type: String,
    default: "utf-8",
  },
});

const allUploadAreDone = ref(false);

function contentTypesMessage() {
  return props.allowedContentTypes.map((e) => e.split("/").at(-1)).join(", ");
}

function humanReadableFileSize(bytes) {
  const units = ['octets', 'Ko', 'Mo', 'Go', 'To'];
  if (bytes === 0) return '0 octets';

  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exp);

  return `${value.toFixed(0)} ${units[exp]}`;
}

async function handleFilesToUpload(files) {
  var numberOfFileToCheck = Array.from(files).length;
  var numberOfFileChecked = 0;

  Array.from(files).forEach((file) => {
    // exclude any files that doesn't match the allowed content types
    if (props.allowedContentTypes.length && !props.allowedContentTypes.includes(file.type)) {
      $q.notify({
        message: "Le fichier " + file.name + " n'est pas un fichier " + contentTypesMessage(),
        type: "negative",
      });
      return;
    }

    if (file.size > props.max_size) {
      const max_size = humanReadableFileSize(props.max_size);
      $q.notify({
        message: i18n.value.upload_file_too_big(file.name, max_size),
        type: "negative",
      });
      return;
    }

    filesToUpload.value.push(file);
    numberOfFileChecked++;
    if (numberOfFileChecked === numberOfFileToCheck) {
      numberOfFilesToUpload.value = filesToUpload.value.length;
      uploadNextFile();
    }
  });
}

// 3 : Start upload
function uploadNextFile() {
  waitBetweenUploads.value = false;
  if (numberOfFilesToUpload.value > 0 && filesToUpload.value.length == 0) {
    // console.log("There is no more files to upload");
    allUploadAreDone.value = true;

    setTimeout(() => {
      allUploadAreDone.value = false;
      resetUploadData();
    }, 2000);
  }

  resetUploadProgress();
  if (filesToUpload.value.length > 0) {
    const file = filesToUpload.value.shift();
    uploadFile(file);
  }
}

const uploadFile = (file) => {
  const workspaceId = page.props.shared?.workspace?.id;
  const uploadUrl = `/rails/active_storage/direct_uploads?workspace_id=${encodeURIComponent(workspaceId || '')}`;
  const upload = new DirectUpload(file, uploadUrl, instance?.proxy);
  upload.create((error, blob) => {
    if (error) {
      $q.notify({
        message: error,
        type: "negative",
      });
    } else {
      onUploadDone(blob);
    }
    waitBetweenUploads.value = true;
    setTimeout(() => {
      uploadNextFile();
    }, 1000);
  });
};

// 4 : Handle of upload progress
// TODO

// 5 : Handle of upload completion
function onUploadDone(blob) {
  // console.log("onUploadDone", blob.filename);
  emit("signed_id", blob, filesToUpload.value.length);
}

function resetUploadProgress() {
  uploadProgress.value = {
    progress: 0,
    total: 0,
    percentile: 0,
  };
}

function resetUploadData() {
  numberOfFilesToUpload.value = 0;
  filesToUpload.value = [];
}
</script>

<style scoped>
input[type="file"] {
  display: none;
}

/* Transition height of the dropzone */
.uploader-area {
  transition: height 0.2s ease-in-out;
}
</style>
